import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface DirectoristListing {
  id: number;
  name: string;
  categories: { id: number; name: string }[];
  locations: { id: number; name: string }[];
  images: { src: string }[];
  average_rating: number;
  rating_count: number;
}

export default function FeaturedProvidersSection() {
  const [featuredProviders, setFeaturedProviders] = useState<DirectoristListing[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Fetch featured providers
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const res = await fetch('https://therevulist.com/wp-json/directorist/v1/listings');
        const data: DirectoristListing[] = await res.json();
        // Pick first 4 providers or you can filter by featured property if exists
        setFeaturedProviders(data.slice(0, 4));
      } catch (err) {
        console.error('Failed to fetch providers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProviders();
  }, []);

  const renderStars = (rating: number) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const stars = [];
    for (let i = 0; i < 5; i++) {
      if (i < fullStars) stars.push(<i key={i} className="ri-star-fill text-yellow-400" />);
      else if (i === fullStars && hasHalfStar) stars.push(<i key={i} className="ri-star-half-fill text-yellow-400" />);
      else stars.push(<i key={i} className="ri-star-line text-yellow-400" />);
    }
    return stars;
  };

  if (loading) {
    return <div className="text-center text-lg py-12">Loading featured providers...</div>;
  }

  if (!featuredProviders.length) {
    return <div className="text-center text-lg py-12 text-gray-500">No featured providers found.</div>;
  }

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Featured Providers</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {featuredProviders.map((provider) => {
            const image = provider.images.length ? provider.images[0].src : 'https://placehold.co/400x400?text=No+Image';
            const specialty = provider.categories[0]?.name || 'N/A';
            const location = provider.locations[0]?.name || 'Unknown';

            return (
              <div
                key={provider.id}
                className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition border"
              >
                <img
                  src={image}
                  alt={provider.name}
                  className="h-48 w-full object-cover rounded-lg mb-4"
                />
                <h3 className="text-xl font-semibold mb-1">{provider.name}</h3>
                <p className="text-sm text-gray-600 mb-2">{specialty}</p>
                <p className="text-sm text-gray-500 mb-3">{location}</p>
                <div className="flex items-center mb-4">{renderStars(provider.average_rating)}
                  <span className="ml-2 text-gray-600 text-sm">({provider.rating_count})</span>
                </div>
                <button
                  className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 rounded-lg transition"
                  onClick={() => navigate(`/provider/${provider.id}`)}
                >
                  View Profile
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
